import { useOTPReset, useRequestOTP } from "@/services/useProfile";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Stack,
  useDisclosure,
  useMergeRefs,
  Heading,
  Image,
  Center,
  Flex,
  FormErrorMessage,
  Tooltip,
  Divider,
  ModalOverlay,
  ModalContent,
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  FormHelperText,
  Link as ChakraLink,
  ModalFooter,
} from "@chakra-ui/react";
import useIsMobile from "@ui/hooks/useIsMobile";
import { Eye, EyeOff, Info } from "lucide-react";
import { forwardRef, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { HelpCircle } from "lucide-react";

const LoginPage = () => {
  const [params] = useSearchParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const returnUrl = params.get("ReturnUrl");
  const username = params.get("username") || "";

  const requestOTP = useRequestOTP();

  const [resetUserName, setResetUserName] = useState<string>(username);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [otp, setOTP] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [userNameError, setuserNameError] = useState<string | null>(null);
  const resetPassword = useOTPReset();

  const handleClose = () => {
    setuserNameError(null);
    setError(null);
    onClose?.();
  };

  const handleRequest = () => {
    if (!resetUserName) {
      setuserNameError("Username is required.");
      return;
    }

    requestOTP.mutate(resetUserName, {
      onSuccess: () => {
        setError(null);
        toast({
          title: "OTP Sent",
          description: "An OTP has been sent to your registered phone number.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setOTP("");
      },
      onError: (err: any) => {
        toast({
          title: "Error",
          description: err?.response?.data?.message || "Failed to send OTP.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one digit.";
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      return "Password must contain at least one special character.";
    }
    const uniqueChars = new Set(password);
    if (uniqueChars.size < 2) {
      return "Password must contain at least one unique character.";
    }
    return null;
  };

  const handleResetPassword = () => {
    if (!otp) {
      setError("OTP is required.");
      return;
    }
    if (!password || !confirmPassword) {
      setError("Password and Confirm Password is required.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    resetPassword.mutate(
      {
        userName: resetUserName,
        otp: otp,
        password: password,
      },
      {
        onSuccess: () => {
          setPassword("");
          setConfirmPassword("");
          setOTP("");
          setResetUserName("");
          onClose?.();

          toast({
            title: "Password Reset",
            description:
              "Your password has been reset successfully. You can now log in with your new password.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        },
        onError: (err: any) => {
          setError(err?.response?.data?.message || "Failed to reset password.");
        },
      },
    );
  };

  const isMobile = useIsMobile();

  return (
    <>
      <Flex
        bgImage={"/images/landing.png"}
        bgPos={"top"}
        bgSize={"cover"}
        bgRepeat={"no-repeat"}
        direction="row"
        h="100vh"
      >
        <Container
          px={{ base: "1", sm: "2" }}
          m="auto"
          height={"max-content"}
          w={isMobile ? 350 : "lg"}
        >
          <Box
            p={4}
            bg={{ base: "white" }}
            boxSizing="border-box"
            borderRadius={"md"}
          >
            <Stack spacing="4">
              <Stack justifyContent={"flex-start"} alignItems={"center"}>
                <Image
                  src="/images/icon.png"
                  alt="MachDatum Logo"
                  w={100}
                  h={100}
                />
              </Stack>
              <Heading
                fontSize={isMobile ? "xl" : "2xl"}
                fontWeight="medium"
                lineHeight={8}
                color="neutrals.1000"
                w={"100%"}
              >
                <Center>Canteen Management System</Center>
              </Heading>
              <form action="/connect/Login" method="POST">
                <Stack spacing="2">
                  <FormControl isInvalid={params.get("error") !== null}>
                    <FormLabel htmlFor="username" color="neutrals.1000">
                      Username (Employee ID)
                    </FormLabel>
                    <Input
                      id="username"
                      name="username"
                      defaultValue={username}
                    />
                  </FormControl>
                  <FormControl isInvalid={params.get("error") !== null}>
                    <PasswordField name="password" />
                    <FormErrorMessage>
                      {params.get("error")}
                      <Tooltip ml={1} label={params.get("message")}>
                        <Box ml={1}>
                          <Info size="1rem" strokeWidth="1.33" />
                        </Box>
                      </Tooltip>
                    </FormErrorMessage>
                  </FormControl>
                  <Input
                    display={"none"}
                    name="returnUrl"
                    value={returnUrl || ""}
                  ></Input>
                  <Button mt={4} variant="primary" type="submit">
                    Sign In
                  </Button>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    align="center"
                    justify="center"
                  >
                    <Alert status="info">
                      <AlertIcon />
                      <AlertDescription fontSize={"12px"}>
                        First-time users should reset their password
                      </AlertDescription>
                    </Alert>
                    <Button variant={"link"} onClick={onOpen}>
                      Reset Password
                    </Button>
                  </Stack>
                  <Center>
                    <ChakraLink
                      as={RouterLink}
                      to="https://www.machdatum.com/support/cms"
                      isExternal
                      display="flex"
                      alignItems="center"
                      fontSize="sm"
                      color="blue.600"
                      mt={2}
                      gap={1}
                    >
                      <HelpCircle size={16} strokeWidth={1.5} />
                      Support
                    </ChakraLink>
                  </Center>
                </Stack>
              </form>
            </Stack>
          </Box>
        </Container>
      </Flex>
      <Modal
        size={isMobile ? "full" : "md"}
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={false}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          width={{ base: "100%", sm: "full" }}
          maxHeight="90vh"
          overflowY="auto"
        >
          <ModalHeader pb={1}>Reset Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={params.get("error") !== null} mb={2}>
              <FormLabel htmlFor="username" color="neutrals.1000">
                Username (Employee ID)
              </FormLabel>
              <Input
                id="username"
                name="username"
                value={resetUserName}
                onChange={(e) => setResetUserName(e.target.value)}
                placeholder="Enter your username"
              />
            </FormControl>
            <Text color={"red.800"} fontSize={"xs"} mb={2}>
              {userNameError}
            </Text>
            <Button colorScheme="blue" w="full" onClick={handleRequest}>
              Send OTP
            </Button>
            <Divider my={4} />
            <FormControl mb={2}>
              <FormLabel htmlFor="otp" color="neutrals.1000">
                Received OTP
              </FormLabel>
              <Input
                id="otp"
                value={otp}
                onChange={(e) => setOTP(e.target.value)}
                name="OTP"
                placeholder="Enter the OTP"
              />
            </FormControl>
            <FormControl mb={2}>
              <FormLabel htmlFor="otp" color="neutrals.1000">
                New Password
              </FormLabel>
              <FormHelperText fontSize="xs" color="gray.500" mb={2}>
                At least 8 characters, 1 uppercase, 1 lowercase, 1 digit, 1
                special character
              </FormHelperText>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New Password"
              />
            </FormControl>
            <FormControl mb={2}>
              <FormLabel htmlFor="otp" color="neutrals.1000">
                Confirm Password
              </FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
              />
            </FormControl>
            <Text color={"red.800"} fontSize={"xs"}>
              {error}
            </Text>
            <Button onClick={handleResetPassword} w="full" mt={2}>
              Reset Password
            </Button>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <ChakraLink
              as={RouterLink}
              to="https://www.machdatum.com/support/cms"
              isExternal
              display="flex"
              alignItems="center"
              fontSize="sm"
              color="blue.600"
              gap={1}
            >
              <HelpCircle size={16} strokeWidth={1.5} />
              Support
            </ChakraLink>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const PasswordField = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const { isOpen, onToggle } = useDisclosure();
    const inputRef = useRef<HTMLInputElement>(null);

    const mergeRef = useMergeRefs(inputRef, ref);
    const onClickReveal = () => {
      onToggle();
      if (inputRef.current) {
        inputRef.current.focus({ preventScroll: true });
      }
    };

    return (
      <>
        <FormLabel htmlFor="password" color="neutrals.1000">
          Password
        </FormLabel>
        <InputGroup>
          <Input
            id="password"
            ref={mergeRef}
            name="password"
            type={isOpen ? "text" : "password"}
            autoComplete="current-password"
            required
            {...props}
          />
          <InputRightElement>
            <IconButton
              variant="text"
              aria-label={isOpen ? "Mask password" : "Reveal password"}
              icon={
                isOpen ? (
                  <EyeOff size={"16px"} strokeWidth={1.33} />
                ) : (
                  <Eye size={"16px"} strokeWidth={1.33} />
                )
              }
              onClick={onClickReveal}
            />
          </InputRightElement>
        </InputGroup>
      </>
    );
  },
);

PasswordField.displayName = "PasswordField";

export default LoginPage;
